import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { ErrorDotIndicator } from '@sharedCR/common/ErrorDotIndicator';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import { TypographyWithSkeleton } from 'app/shared/componentsRestyled/coreDesignSystem/Typography';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import {
  additionalAfterStyles,
  getTabStyles,
  labelStyles,
  labelWrapperStyles,
  pendingChangesCounterStyles,
  tabsStyles,
  wrapperStyles,
} from './EntityFormModalHead.styles';
import { EntityFormModalHeadTestIds } from './EntityFormModalHead.testIds';

const defaultValue = 'loadingValue';

const defaultTabs = [
  {
    value: defaultValue,
    label: '',
  },
];

const EntityFormModalHead = ({ onClose, tabs, value }) => {
  const tabsRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const isLoading = !value || !tabs?.length;
  const tabsToShow = isLoading ? defaultTabs : tabs;

  const onWheel = useCallback(e => {
    if (!scrollContainerRef.current) {
      return;
    }

    scrollContainerRef.current.scrollLeft += e.deltaY;
  }, []);

  useEffect(() => {
    if (!tabsRef.current) {
      return;
    }

    scrollContainerRef.current = tabsRef.current?.querySelector('.MuiTabs-scroller');
  }, [tabs]);

  return (
    <Box
      className="head"
      sx={wrapperStyles}
      onMouseLeave={onResetTooltipState}
      onMouseOver={onMouseOver}
    >
      <TruncatedProvider />

      {(!!tabs.length || isLoading) && (
        <Tabs
          className="tabs"
          indicatorColor="transparent"
          ref={tabsRef}
          scrollButtons={false}
          sx={tabsStyles}
          value={isLoading ? defaultValue : value}
          variant="scrollable"
          onWheel={onWheel}
        >
          {tabsToShow.map(tab => (
            <Tab
              disableRipple
              className="tab"
              key={tab.value}
              label={
                <Box sx={labelWrapperStyles}>
                  {!!tab.pendingChangesCount && (
                    <PendingChangeWrapper
                      isSmall
                      className="pending-changes-count-wrapper"
                      pendingChangesActionType={PendingChangesActionType.MODIFIED}
                      sx={pendingChangesCounterStyles}
                    >
                      <p className="pending-changes-count">{tab.pendingChangesCount}</p>
                    </PendingChangeWrapper>
                  )}
                  <PendingChangeWrapper
                    isSmall
                    pendingChangesActionType={
                      tab.isModified ? PendingChangesActionType.MODIFIED : undefined
                    }
                    withBackground={false}
                  >
                    <TypographyWithSkeleton
                      data-truncated
                      className="label"
                      component="p"
                      isLoading={isLoading}
                      skeletonWidth={130}
                      sx={labelStyles}
                      variant="medium16"
                    >
                      {tab.label}
                    </TypographyWithSkeleton>
                  </PendingChangeWrapper>
                  {tab.isInvalid && <ErrorDotIndicator />}
                  <Typography component="span" sx={additionalAfterStyles} variant="regular14" />
                </Box>
              }
              sx={getTabStyles(tab.customStyles)}
              value={isLoading ? defaultValue : tab.value}
              onClick={tab.onClick}
            />
          ))}
        </Tabs>
      )}

      <IconButton
        disableRipple
        className="close-button"
        color="text.dark"
        data-testid={EntityFormModalHeadTestIds.CLOSE_BUTTON}
        onClick={onClose}
      >
        <CancelIcon size={IconSize[20]} />
      </IconButton>
    </Box>
  );
};

EntityFormModalHead.propTypes = {
  tabs: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  value: PropTypes.string,
};

export default memo(EntityFormModalHead);
